import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Code, 
  Smartphone, 
  ShoppingCart, 
  Search, 
  Shield, 
  Database, 
  Settings, 
  CreditCard, 
  BarChart3, 
  Cloud, 
  Palette, 
  Monitor, 
  Globe,
  CheckCircle,
  Star,
  Users,
  TrendingUp,
  Headphones
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../components/layout/Sidebar";

const Development = () => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const title = "Professional Web Development & Digital Solutions";
  const description = "Transform your business with cutting-edge web development, custom applications, and comprehensive digital solutions. From responsive websites to complex POS systems - we build technology that drives growth.";

  const services = {
    webDevelopment: {
      title: "Custom Web Development",
      icon: <Code className="w-8 h-8" />,
      description: "Full-stack web applications built with modern technologies",
      features: [
        "Custom React.js & Next.js Applications",
        "Progressive Web Apps (PWAs)",
        "Single Page Applications (SPAs)",
        "Server-side Rendering (SSR)",
        "API Development & Integration",
        "Database Design & Optimization",
        "Real-time Applications with WebSockets",
        "Microservices Architecture"
      ]
    },
    mobileApps: {
      title: "Mobile App Development",
      icon: <Smartphone className="w-8 h-8" />,
      description: "Native and cross-platform mobile applications",
      features: [
        "React Native Cross-platform Apps",
        "Native iOS & Android Development",
        "Flutter Applications",
        "Mobile-first UI/UX Design",
        "Push Notifications Integration",
        "Offline Functionality",
        "App Store Optimization",
        "Mobile Analytics Implementation"
      ]
    },
    ecommerce: {
      title: "E-commerce Solutions",
      icon: <ShoppingCart className="w-8 h-8" />,
      description: "Complete online store solutions with advanced features",
      features: [
        "Custom E-commerce Platforms",
        "WooCommerce & Shopify Development",
        "Multi-vendor Marketplaces",
        "Inventory Management Systems",
        "Payment Gateway Integration",
        "Order Management & Tracking",
        "Customer Relationship Management",
        "Analytics & Reporting Dashboards"
      ]
    },
    posSystem: {
      title: "POS System Development",
      icon: <CreditCard className="w-8 h-8" />,
      description: "Custom Point of Sale systems for retail and hospitality",
      features: [
        "Cloud-based POS Solutions",
        "Offline Capability with Sync",
        "Inventory Management Integration",
        "Multi-location Support",
        "Staff Management & Permissions",
        "Sales Analytics & Reporting",
        "Customer Loyalty Programs",
        "Integration with Accounting Software",
        "Receipt Printing & Email",
        "Barcode & QR Code Scanning",
        "Payment Processing Integration",
        "Real-time Dashboard Monitoring"
      ]
    },
    seoOptimization: {
      title: "SEO & Performance",
      icon: <Search className="w-8 h-8" />,
      description: "Advanced SEO strategies and performance optimization",
      features: [
        "Technical SEO Audits",
        "On-page SEO Optimization",
        "Core Web Vitals Optimization",
        "Schema Markup Implementation",
        "Local SEO for Businesses",
        "Mobile-first Indexing",
        "Site Speed Optimization",
        "SEO Analytics & Monitoring"
      ]
    },
    cloudServices: {
      title: "Cloud & DevOps",
      icon: <Cloud className="w-8 h-8" />,
      description: "Scalable cloud infrastructure and deployment solutions",
      features: [
        "AWS & Azure Cloud Deployment",
        "Docker Containerization",
        "CI/CD Pipeline Setup",
        "Auto-scaling Infrastructure",
        "Database Migration & Management",
        "Backup & Disaster Recovery",
        "Monitoring & Alerting Systems",
        "Security Implementation"
      ]
    }
  };

  const configurationsServices = [
    {
      title: "Server Configuration & Management",
      items: [
        "Linux Server Setup & Hardening",
        "Nginx & Apache Configuration",
        "SSL Certificate Installation",
        "Domain & DNS Management",
        "Email Server Configuration",
        "Database Server Optimization",
        "Firewall & Security Configuration",
        "Backup Automation Setup"
      ]
    },
    {
      title: "Application Configuration",
      items: [
        "Environment Configuration Management",
        "API Keys & Secrets Management",
        "Third-party Service Integration",
        "Payment Gateway Configuration",
        "Social Media Login Setup",
        "Analytics Tools Integration",
        "CDN Configuration",
        "Caching Strategy Implementation"
      ]
    },
    {
      title: "Development Environment Setup",
      items: [
        "Local Development Environment",
        "Version Control (Git) Setup",
        "Code Quality Tools Configuration",
        "Testing Environment Setup",
        "Staging Server Configuration",
        "Production Deployment Pipeline",
        "Monitoring & Logging Setup",
        "Performance Monitoring Tools"
      ]
    }
  ];

  const whyChooseUs = [
    {
      icon: <Star className="w-6 h-6 text-yellow-400" />,
      title: "5+ Years Experience",
      description: "Proven track record with 200+ successful projects"
    },
    {
      icon: <Users className="w-6 h-6 text-blue-400" />,
      title: "Expert Team",
      description: "Skilled developers, designers, and project managers"
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-green-400" />,
      title: "Growth-Focused",
      description: "Solutions designed to scale with your business"
    },
    {
      icon: <Headphones className="w-6 h-6 text-purple-400" />,
      title: "24/7 Support",
      description: "Ongoing maintenance and technical support"
    }
  ];

  const gallery = [
    "/images/webdev/1.jpeg",
    "/images/webdev/2.jpeg",
    "/images/webdev/3.jpeg",
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Monitor className="w-4 h-4" /> },
    { id: 'services', label: 'Services', icon: <Settings className="w-4 h-4" /> },
    { id: 'pos', label: 'POS Systems', icon: <CreditCard className="w-4 h-4" /> },
    { id: 'configuration', label: 'Configuration', icon: <Database className="w-4 h-4" /> },
    { id: 'portfolio', label: 'Portfolio', icon: <Palette className="w-4 h-4" /> }
  ];

  return (
    <div className="pt-20 bg-gradient-to-br from-slate-200 via-blue-700 to-indigo-200 min-h-screen">
      <Helmet>
        <title>{title} | Northstart Systems</title>
        <meta name="description" content={description} />
        <meta
          name="keywords"
          content="web development kenya, custom software development, POS system development, e-commerce development, mobile app development, react development, SEO optimization, cloud services, meru developers"
        />
        <meta property="og:title" content={`${title} | Northstart Systems`} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="/images/webdev/1.jpeg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} | Northstart Systems`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="/images/webdev/1.jpeg" />
        <link rel="canonical" href="https://northstartsystems.co.ke/services/development" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 flex gap-8">
        <Sidebar />
        
        <main className="flex-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section */}
          <div className="p-8 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-b border-white/20">
            <Link
              to="/services"
              className="inline-flex items-center text-cyan-300 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Services
            </Link>
            
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-5xl font-bold mb-4 text-white leading-tight">
                  {title}
                </h1>
                <p className="text-xl text-slate-200 mb-6 leading-relaxed max-w-3xl">
                  {description}
                </p>
                
                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center bg-green-500/20 px-4 py-2 rounded-full">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                    <span className="text-green-200">Available 24/7</span>
                  </div>
                  <div className="flex items-center bg-blue-500/20 px-4 py-2 rounded-full">
                    <Globe className="w-5 h-5 text-blue-400 mr-2" />
                    <span className="text-blue-200">Global Reach</span>
                  </div>
                  <div className="flex items-center bg-purple-500/20 px-4 py-2 rounded-full">
                    <Shield className="w-5 h-5 text-purple-400 mr-2" />
                    <span className="text-purple-200">Secure & Reliable</span>
                  </div>
                </div>
              </div>
              
              <div className="hidden lg:block">
                <div className="bg-white/10 p-6 rounded-2xl border border-white/20">
                  <h3 className="text-lg font-semibold text-white mb-4">Quick Stats</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-300">Projects Completed</span>
                      <span className="text-cyan-300 font-semibold">200+</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Years Experience</span>
                      <span className="text-cyan-300 font-semibold">5+</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Client Satisfaction</span>
                      <span className="text-cyan-300 font-semibold">99%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="px-8 py-4 bg-white/5 border-b border-white/10">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-3 rounded-xl transition-all ${
                    activeTab === tab.id
                      ? 'bg-cyan-500/30 text-white border border-cyan-400/50'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {tab.icon}
                  <span className="ml-2 font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="p-8 text-white">
            {activeTab === 'overview' && (
              <div className="space-y-12">
                {/* Why Choose Us */}
                <section>
                  <h2 className="text-3xl font-bold mb-8 text-center">Why Choose Northstart Systems?</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {whyChooseUs.map((item, idx) => (
                      <div key={idx} className="text-center p-6 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
                        <div className="flex justify-center mb-4">
                          {item.icon}
                        </div>
                        <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                        <p className="text-slate-300 text-sm">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Technology Stack */}
                <section>
                  <h2 className="text-3xl font-bold mb-8">Our Technology Stack</h2>
                  <div className="grid md:grid-cols-3 gap-8">
                    <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 p-6 rounded-2xl border border-orange-500/20">
                      <h3 className="text-xl font-semibold mb-4 text-orange-300">Frontend</h3>
                      <ul className="space-y-2 text-slate-300">
                        <li>• React.js & Next.js</li>
                        <li>• Vue.js & Nuxt.js</li>
                        <li>• TypeScript</li>
                        <li>• Tailwind CSS</li>
                        <li>• Progressive Web Apps</li>
                      </ul>
                    </div>
                    <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 p-6 rounded-2xl border border-green-500/20">
                      <h3 className="text-xl font-semibold mb-4 text-green-300">Backend</h3>
                      <ul className="space-y-2 text-slate-300">
                        <li>• Node.js & Express</li>
                        <li>• Python & Django</li>
                        <li>• PHP & Laravel</li>
                        <li>• RESTful APIs</li>
                        <li>• GraphQL</li>
                      </ul>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 p-6 rounded-2xl border border-blue-500/20">
                      <h3 className="text-xl font-semibold mb-4 text-blue-300">Database & Cloud</h3>
                      <ul className="space-y-2 text-slate-300">
                        <li>• MongoDB & PostgreSQL</li>
                        <li>• AWS & Azure</li>
                        <li>• Docker & Kubernetes</li>
                        <li>• Redis Caching</li>
                        <li>• CI/CD Pipelines</li>
                      </ul>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold mb-8">Our Comprehensive Services</h2>
                <div className="grid gap-8">
                  {Object.values(services).map((service, idx) => (
                    <div key={idx} className="bg-white/5 rounded-2xl border border-white/10 p-8 hover:bg-white/10 transition-all">
                      <div className="flex items-start gap-6">
                        <div className="flex-shrink-0 p-4 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl border border-cyan-500/30">
                          {service.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold mb-3">{service.title}</h3>
                          <p className="text-slate-300 mb-6 text-lg">{service.description}</p>
                          <div className="grid md:grid-cols-2 gap-3">
                            {service.features.map((feature, featureIdx) => (
                              <div key={featureIdx} className="flex items-center">
                                <CheckCircle className="w-4 h-4 text-green-400 mr-3 flex-shrink-0" />
                                <span className="text-slate-200">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'pos' && (
              <div className="space-y-8">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold mb-4">Advanced POS System Development</h2>
                  <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                    Transform your retail or hospitality business with our custom Point of Sale systems. 
                    Built for performance, reliability, and growth.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-2xl border border-cyan-500/20 p-8 mb-8">
                  <div className="flex items-center mb-6">
                    <CreditCard className="w-10 h-10 text-cyan-400 mr-4" />
                    <h3 className="text-3xl font-semibold">Complete POS Solutions</h3>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {services.posSystem.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                    <BarChart3 className="w-8 h-8 text-purple-400 mb-4" />
                    <h4 className="text-lg font-semibold mb-3">Retail POS</h4>
                    <p className="text-slate-300 text-sm mb-4">
                      Perfect for retail stores, boutiques, and shops with inventory management and customer tracking.
                    </p>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Barcode scanning</li>
                      <li>• Inventory tracking</li>
                      <li>• Customer loyalty</li>
                      <li>• Multi-payment options</li>
                    </ul>
                  </div>

                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                    <Users className="w-8 h-8 text-orange-400 mb-4" />
                    <h4 className="text-lg font-semibold mb-3">Restaurant POS</h4>
                    <p className="text-slate-300 text-sm mb-4">
                      Specialized for restaurants, cafes, and food service with table management and kitchen integration.
                    </p>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Table management</li>
                      <li>• Kitchen display system</li>
                      <li>• Order tracking</li>
                      <li>• Staff management</li>
                    </ul>
                  </div>

                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                    <Globe className="w-8 h-8 text-green-400 mb-4" />
                    <h4 className="text-lg font-semibold mb-3">Multi-Location POS</h4>
                    <p className="text-slate-300 text-sm mb-4">
                      Enterprise solutions for businesses with multiple locations and centralized management.
                    </p>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Centralized dashboard</li>
                      <li>• Real-time reporting</li>
                      <li>• Inventory sync</li>
                      <li>• Remote management</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'configuration' && (
              <div className="space-y-8">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold mb-4">Professional Configuration Services</h2>
                  <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                    Complete setup and configuration services to get your systems running smoothly 
                    and securely from day one.
                  </p>
                </div>

                <div className="grid gap-8">
                  {configurationsServices.map((section, idx) => (
                    <div key={idx} className="bg-white/5 rounded-2xl border border-white/10 p-8">
                      <h3 className="text-2xl font-semibold mb-6 flex items-center">
                        <Settings className="w-6 h-6 text-cyan-400 mr-3" />
                        {section.title}
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {section.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="flex items-center p-3 bg-white/5 rounded-xl">
                            <CheckCircle className="w-4 h-4 text-green-400 mr-3 flex-shrink-0" />
                            <span className="text-slate-200">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl border border-purple-500/20 p-8">
                  <h3 className="text-2xl font-semibold mb-4">Configuration Process</h3>
                  <div className="grid md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-purple-300 font-bold">1</span>
                      </div>
                      <h4 className="font-semibold mb-2">Analysis</h4>
                      <p className="text-sm text-slate-300">Assess your requirements and current setup</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-purple-300 font-bold">2</span>
                      </div>
                      <h4 className="font-semibold mb-2">Planning</h4>
                      <p className="text-sm text-slate-300">Create detailed configuration plan</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-purple-300 font-bold">3</span>
                      </div>
                      <h4 className="font-semibold mb-2">Implementation</h4>
                      <p className="text-sm text-slate-300">Execute configuration with minimal downtime</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-purple-300 font-bold">4</span>
                      </div>
                      <h4 className="font-semibold mb-2">Testing</h4>
                      <p className="text-sm text-slate-300">Thorough testing and optimization</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'portfolio' && (
              <div className="space-y-8">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold mb-4">Our Work Portfolio</h2>
                  <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                    Explore some of our recent projects and see how we've helped businesses 
                    transform their digital presence.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                  {gallery.map((img, idx) => (
                    <div key={idx} className="group relative overflow-hidden rounded-2xl">
                      <img
                        src={img}
                        alt={`Portfolio project ${idx + 1}`}
                        className="w-full h-64 object-cover transition-transform group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-white font-semibold mb-1">Project {idx + 1}</h3>
                          <p className="text-slate-200 text-sm">Custom web development solution</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-2xl border border-cyan-500/20 p-8 text-center">
                  <h3 className="text-2xl font-semibold mb-4">Ready to Start Your Project?</h3>
                  <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                    Let's discuss your requirements and create a custom solution that drives your business forward. 
                    Get in touch for a free consultation.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <button className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-cyan-600 hover:to-blue-700 transition-all transform hover:scale-105">
                      Start Your Project
                    </button>
                    <button className="bg-white/10 text-white px-8 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/20">
                      View More Work
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Development;